import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import HeroSection from './components/HeroSection';
import SeminarOverview from './components/SeminarOverview';
import FeeCarryOver from './components/FeeCarryOver';
import ProgramTimeline from './components/ProgramTimeline';
import GainSection from './components/GainSection';
import WhatToBring from './components/WhatToBring';
import CourseIntro from './components/CourseIntro';
import ApplicationForm from './components/ApplicationForm';
import ImportantNotes from './components/ImportantNotes';
import CTASection from './components/CTASection';
import RelatedPages from './components/RelatedPages';
import FAQSection from './components/FAQSection';
import Seo from '@/components/seo/Seo';

export default function SeminarPage() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const { lang } = useLanguageContext();
  const { t } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: lang === 'en' ? 'Home' : 'ホーム', item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: lang === 'en' ? 'Admissions' : '入学案内', item: 'https://butler-academy.jp/admission' },
      { '@type': 'ListItem', position: 3, name: lang === 'en' ? 'One-Day Taster Session' : '体験講座', item: 'https://butler-academy.jp/seminar' },
    ],
  };

  const eventData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: lang === 'en' ? "One-Day Taster Session: Introduction to Butler Etiquette" : "体験講座『執事の作法・入門』",
    description: lang === 'en'
      ? 'A one-day, six-hour introductory programme in which you wear white gloves to experience authentic butler education — from deportment to tea service.'
      : '執事の所作とティーサービスを1日6時間で体験できる入門プログラム。白手袋を着けて本物の執事教育を体験いただけます。',
    organizer: {
      '@type': 'EducationalOrganization',
      name: lang === 'en' ? 'The All Japan Butler Association' : '一般社団法人全日本執事協会',
    },
    location: {
      '@type': 'Place',
      name: lang === 'en' ? 'Japan Butler Academy — Main Campus' : '日本執事アカデミー 本校',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2-7-4 Aomi',
        addressLocality: lang === 'en' ? 'Koto-ku, Tokyo' : '東京都江東区',
        postalCode: '135-0064',
        addressCountry: 'JP',
      },
    },
    offers: {
      '@type': 'Offer',
      price: '33000',
      priceCurrency: 'JPY',
      availability: 'https://schema.org/InStock',
    },
    eventStatus: 'https://schema.org/EventScheduled',
  };

  return (
    <>
      <Seo route="/seminar" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(eventData) }}
      />

      <Header />

      <main>
        <HeroSection />
        <SeminarOverview />
        <FeeCarryOver />
        <ProgramTimeline />
        <GainSection />
        <WhatToBring />
        <CourseIntro />
        <ApplicationForm />
        <ImportantNotes />
        <CTASection />
        <RelatedPages />
        <FAQSection />

        {/* Last Updated */}
        <section className="py-8 bg-background-50">
          <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 text-center">
            {String(t('seminar.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-500 tracking-[0.05em]">{__s}</p>))}
          </div>
        </section>
      </main>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 z-40 w-11 h-11 rounded-full border border-accent-500/40 bg-primary-900/80 backdrop-blur-sm text-accent-400 hover:text-accent-300 hover:border-accent-400 hover:bg-primary-900 transition-all duration-300 flex items-center justify-center ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        aria-label={t('common.backToTop')}
      >
        <i className="ri-arrow-up-line text-lg"></i>
      </button>
    </>
  );
}
